import React, { useCallback, useMemo } from 'react';
import { View, ScrollView, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { DesktopMainParamList } from '@/navigation/residents/desktop/DesktopNavigator';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useHomeScreen } from '@/hooks/residents/useHomeScreen';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAppSelector } from '@/store';
import { formatRelativeDate } from '@/utils/common/formatting';
import { getBalanceStatus } from '@/features/payments/common/helpers';
import { DesktopHomeViewSkeleton } from './DesktopHomeViewSkeleton';
import {
  GreetingSection,
  QuickActionsSection,
  ComingUpSection,
  CommunityResourcesSection,
  TodoCalendarSection,
  type ComingUpNextEvent,
  type ComingUpRentDue,
} from '@/components/residents/redesign';

const CONTENT_PADDING = REDESIGN_SPACING.horizontalPadding;
const INNER_GAP = 16;
/**
 * Floor for the planner column. Same failure as mobile — a squeezed column and
 * GlassCard's `overflow: hidden` clip the tabs, the day heading and "+ Add" —
 * but the trigger here is a short browser window rather than a small phone,
 * and `columns`' own `minHeight: 0` is what lets it shrink that far.
 *
 * Mobile's arithmetic plus a second row, because a desktop planner showing one
 * todo is not a planner: dense header 32 + card chrome 158 (16pt padding
 * top/bottom, 48pt tabs, 44pt heading row, two 16pt gaps, 2pt border) + two
 * ~56pt rows = 302, rounded up to 320. Under that the page scrolls (below).
 */
const PLANNER_COLUMN_MIN_HEIGHT = 320;

/**
 * Desktop home. Like mobile, the page is sized to fit rather than to scroll:
 * the greeting sits on top of a flexed row whose left column is the planner
 * (stretched, its todo list scrolling internally) and whose right column is a
 * narrow scroller for the three card sections — a scroller only so a short
 * browser window degrades gracefully, not because the page is expected to
 * overflow.
 *
 * The page container is a ScrollView for the same reason, and behaves the same
 * way as MobileHomeView's: `flexGrow: 1` content and no bounce, so it does not
 * scroll at all while everything fits. It engages only when a genuinely short
 * window cannot give the planner column its floor — at which point scrolling is
 * the honest outcome, and far better than clipping the card away.
 */
export const DesktopHomeView: React.FC = () => {
  const navigation = useNavigation<StackNavigationProp<DesktopMainParamList>>();
  const { isTablet } = useResponsive();
  const isHomeLoading = useAppSelector((s) => s.home.isLoading);

  const {
    user,
    greeting,
    firstName,
    unitName,
    newsfeedNewCount,
    unreadMessagesCount,
    surveysToAnswerCount,
    nextUpcomingEvent,
    balanceDue,
    openTickets,
    refreshing,
    shouldShowPayments,
  } = useHomeScreen();

  const handlePaymentsPress = useCallback(() => {
    if (user?.currentLeaseId) {
      navigation.navigate('PaymentSummaryModal', {
        leaseId: user.currentLeaseId,
        residentId: user.residentId,
      });
    } else {
      navigation.navigate('Payments');
    }
  }, [navigation, user?.currentLeaseId, user?.residentId]);
  const handleMaintenancePress = () => navigation.navigate('Maintenance');
  const handlePlannerPress = () => navigation.navigate('Planner');
  const handleBulletinsPress = () => navigation.navigate('CommunityBoard');
  // Desktop has no per-event route: the events list is the deepest link there
  // is, so both the Coming Up row and the My Community tile go there.
  const handleEventsPress = () => navigation.navigate('Events');
  const handleMarketplacePress = () => navigation.navigate('Marketplace');
  const handleResourcesPress = () => navigation.navigate('Resources');
  const handleLinkedAccountsPress = () => navigation.navigate('LinkedAccounts');
  const handleMessagesPress = () => navigation.navigate('Messages');
  const handleWifiPress = () => navigation.navigate('Wifi');

  const handleSurveysPress = () => {
    navigation.navigate('Surveys', { showBackLink: true });
  };

  // Amount only — see HomeScreen: no rent due date is available here. A
  // negative balance is a credit and still gets a row; the status is decided
  // here (it reads the day of the month) so the section stays a pure presenter.
  const rentDue = useMemo<ComingUpRentDue | null>(
    () =>
      shouldShowPayments && Number.isFinite(balanceDue) && balanceDue !== 0
        ? { amount: balanceDue, status: getBalanceStatus(balanceDue) }
        : null,
    [shouldShowPayments, balanceDue],
  );

  const nextEvent = useMemo<ComingUpNextEvent | null>(() => {
    if (!nextUpcomingEvent) return null;
    return {
      title: nextUpcomingEvent.name || 'Community event',
      dateLabel: formatRelativeDate(
        nextUpcomingEvent.startTimeLocal || nextUpcomingEvent.startTime,
      ),
    };
  }, [nextUpcomingEvent]);

  const useStackedLayout = isTablet;

  return (
    <ScrollView
      style={styles.page}
      contentContainerStyle={styles.pageContent}
      // No bounce: while the content fits, this must feel like the fixed page
      // it is, not a scroller.
      alwaysBounceVertical={false}
      showsVerticalScrollIndicator={false}
    >
      {isHomeLoading && !refreshing ? (
        <DesktopHomeViewSkeleton isTablet={isTablet} />
      ) : (
        <>
          <GreetingSection
            greeting={greeting}
            firstName={firstName}
            unitName={unitName}
            style={styles.greeting}
          />

          <View
            style={[
              styles.columns,
              useStackedLayout ? styles.stackedColumns : styles.rowColumns,
            ]}
          >
            <View style={styles.columnPrimary}>
              <TodoCalendarSection
                fill
                denseHeader
                onViewAll={handlePlannerPress}
              />
            </View>

            <ScrollView
              style={styles.columnSecondary}
              contentContainerStyle={styles.columnSecondaryContent}
              showsVerticalScrollIndicator={false}
              // This column now sits inside the page scroller above, so on
              // Android the parent would otherwise swallow its gestures. (The
              // planner's own list already sets this for itself.)
              nestedScrollEnabled
            >
              <QuickActionsSection
                openTicketsCount={openTickets?.length || 0}
                newsfeedNewCount={newsfeedNewCount}
                unreadMessagesCount={unreadMessagesCount}
                surveysCount={surveysToAnswerCount}
                onMaintenance={handleMaintenancePress}
                onNewsfeed={handleBulletinsPress}
                onMessages={handleMessagesPress}
                onSurveys={handleSurveysPress}
                layout="grid"
                denseHeader
              />

              <ComingUpSection
                rentDue={rentDue}
                onRentDuePress={handlePaymentsPress}
                nextEvent={nextEvent}
                onNextEventPress={handleEventsPress}
                denseHeader
              />

              <CommunityResourcesSection
                onMarketplace={handleMarketplacePress}
                onResources={handleResourcesPress}
                onEvents={handleEventsPress}
                onWifi={handleWifiPress}
                onLinkedAccountsPress={handleLinkedAccountsPress}
                layout="grid"
                denseHeader
              />
            </ScrollView>
          </View>
        </>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  pageContent: {
    // Exactly one window tall while everything fits (so the planner column's
    // flex has room to stretch into and nothing scrolls); taller only when the
    // floored planner no longer fits.
    flexGrow: 1,
    padding: CONTENT_PADDING,
  },
  greeting: {
    // The page already pads 24 all round; the section's own 24 on top of that
    // eats height the planner wants.
    paddingTop: 0,
    marginBottom: INNER_GAP,
  },
  columns: {
    // minHeight:0 so this row can shrink below its content height on web
    // instead of pushing the page taller than the window. The planner column
    // inside it still stops at its own floor; that is the point where the page
    // scroller engages.
    flex: 1,
    minHeight: 0,
    gap: INNER_GAP,
  },
  rowColumns: {
    flexDirection: 'row',
  },
  // Tablet fallback. RootNavigator swaps to the mobile navigator below 1024px,
  // so this branch is effectively unreachable — it just has to stay sane.
  stackedColumns: {
    flexDirection: 'column',
  },
  columnPrimary: {
    flex: 3,
    // A floor, not `minHeight: 0`: the column may stretch, never collapse. See
    // PLANNER_COLUMN_MIN_HEIGHT — past it the page ScrollView takes over.
    minHeight: PLANNER_COLUMN_MIN_HEIGHT,
  },
  columnSecondary: {
    flex: 2,
    minHeight: 0,
  },
  columnSecondaryContent: {
    gap: INNER_GAP,
    paddingBottom: INNER_GAP,
  },
});

export default DesktopHomeView;
